import { FieldValues } from 'react-hook-form';
import { ListItemProps } from './props';
import styles from './styles.module.scss';
import { memo } from 'react';

/**
 * Component that renders a single option item in the options list
 *
 * @template T - Type extending FieldValues from react-hook-form
 * @template V - Type of the option value
 *
 * @param {Object} props - Component props
 * @param {Function} props.getOptionLabel - Function to get display label from option
 * @param {Function} props.getOptionValue - Function to get value from option
 * @param {T} props.option - The option data to render
 * @param {Function} props.setValue - Function to set the selected value
 * @param {Object} props.customComponents - Custom component overrides
 * @param {boolean} props.loading - Whether the item is in loading state
 * @param {Function} props.setOpen - Function to control options drawer visibility
 * @param {V} props.value - Currently selected value
 * @returns {JSX.Element} List item component
 */
function ListItem<T extends FieldValues, V>({
  getOptionLabel,
  getOptionValue,
  option,
  setValue,
  customComponents,
  loading,
  setOpen,
  value,
}: ListItemProps<T, V>) {
  /**
   * Handles selecting an option
   * Sets the selected value and closes the options drawer
   *
   * @param {T} value - The option value to select
   */
  function handleSelectFilter(value: T) {
    setValue(value);
    setOpen(false);
  }

  // Show loading state or custom skeleton
  if (loading || !option) {
    if (!!customComponents?.customSkeletonOption) {
      return customComponents.customSkeletonOption?.();
    }

    return (
      <div className={`${styles.container} ${styles['container--loading']}`}>
        <div className={styles.skeleton} />
      </div>
    );
  }

  // Check if this option is currently selected
  const selected: boolean = getOptionValue(option) === value;

  // Use custom option component if provided
  if (!!customComponents?.customOption) {
    return customComponents?.customOption({
      option,
      selected: getOptionValue(option) === value,
      handleSelect: handleSelectFilter,
    });
  }

  // Default option rendering
  return (
    <div
      className={`${styles.container} ${selected ? styles['container--selected'] : ''}`}
      onClick={() => handleSelectFilter(option)}
    >
      {getOptionLabel(option)}
    </div>
  );
}

export default memo(ListItem) as typeof ListItem;
